import type { CreatorGroup } from "@/shared/types/campaign/creator";
import { CreatorInfo } from "./creator";
import { axiosInstance } from "./index";
import axios from "axios";
import type { CreatorInterestStatus } from "@/shared/types/campaign/creatorInterestStatus";

export interface CreatorList {
	id: string;
	campaignId: string;
	blockId: string;
}

export interface CreatorOutreach {
	id: string;
	campaignId: string;
	blockId: string;
}

export interface CreatorAssignment {
	id: string;
	campaignId: string;
	blockId: string;
	groupId: string[]; // Changed from string to string[]
	outreachId: string;
	listId: string;
	creatorId: string;
	creatorInterested: CreatorInterestStatus | undefined;
	finalRate: number | null;
	finalConvertedRate: number | null;
	clientVisible: boolean;
	addedToCampaign: boolean;
	recommendation: string[];
	clientHiddenFields: string[];
	hiddenFields: string[];
	contactEmail?: string | null;
}

export interface GroupApproval {
	id: string;
	groupId: string;
	userId: string;
	status: string;
	comment: string;
	createdAt: string;
	updatedAt: string;
	authorName?: string;
	authorImageUrl?: string;
}

export interface CreatorAssignmentApproval {
	id: string;
	assignmentId: string;
	userId: string;
	status: string;
	comment: string;
	createdAt: string;
	updatedAt: string;
	authorName?: string;
	authorImageUrl?: string | null;
	authorRoles?: string[];
}

export interface CreatorWithAssignment {
	creator: CreatorInfo;
	assignment: CreatorAssignment;
}

export interface CreatorWithAssignmentResponse {
	creator: CreatorInfo;
	assignment: CreatorAssignment;
	socialAccounts: Record<
		string,
		{
			url: string;
			avatarUrl: string;
			platformUsername: string;
			followers: number;
			avgEngagementRate: number;
			avgViews: number;
			localAudiencePercentage: number | null;
			femalePercentage: number;
			malePercentage: number;
			audienceAgeDistribution: Array<{ ageRange: string; percentage: number }>;
		}
	>;
	customInfo: Record<string, string>;
}

export interface SettingData {
	clientVisible: boolean;
	include: boolean;
	index: number;
}

export interface CreatorDisplaySettingResponse {
	id: string;
	displayType: string;
	displayId: string;
	setting: Record<string, any>;
}

export const creatorListAPI = {
	/**
	 * Get creator lists for a specific campaign block
	 */
	async getCreatorLists(campaignId: string, blockId: string): Promise<CreatorList> {
		try {
			const response = await axiosInstance.get<CreatorList>(
				`/campaign/${campaignId}/block/${blockId}/creator-lists`,
			);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creator lists for campaign ${campaignId}, block ${blockId}:`, error);
			throw new Error("Failed to fetch creator lists");
		}
	},

	/**
	 * Get creator outreaches for a specific campaign block
	 */
	async getCreatorOutreaches(campaignId: string, blockId: string): Promise<CreatorOutreach[]> {
		try {
			const response = await axiosInstance.get<CreatorOutreach[]>(
				`/campaign/${campaignId}/block/${blockId}/creator-outreaches`,
			);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creator outreaches for campaign ${campaignId}, block ${blockId}:`, error);
			throw new Error("Failed to fetch creator outreaches");
		}
	},

	/**
	 * Get creator groupings for a specific campaign block
	 */
	async getCreatorGroups(campaignId: string, blockId: string): Promise<CreatorGroup[]> {
		try {
			const response = await axiosInstance.get(`/campaign/${campaignId}/block/${blockId}/creator-groups`);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creator groupings for campaign ${campaignId}, block ${blockId}:`, error);
			throw new Error("Failed to fetch creator groupings");
		}
	},

	/**
	 * Get campaign rate (currency information)
	 */
	async getCampaignRate(
		campaignId: string,
	): Promise<{ finalCurrency: string; finalConvertedCurrency: string } | null> {
		try {
			const response = await axiosInstance.get(`/campaign/${campaignId}/campaign-rate`);
			return response.data;
		} catch (error) {
			console.error(`Error fetching campaign rate for campaign ${campaignId}:`, error);
			throw error;
		}
	},

	/**
	 * Update display settings for a specific display
	 * Sends the complete CreatorDisplaySetting object
	 */
	async updateDisplaySettings(
		campaignId: string,
		blockId: string,
		displayId: string,
		displaySetting: CreatorDisplaySettingResponse,
	): Promise<CreatorDisplaySettingResponse> {
		try {
			console.log("📤 Updating display settings with full object:", displaySetting);
			const response = await axiosInstance.put<CreatorDisplaySettingResponse>(
				`/campaign/${campaignId}/block/${blockId}/creator-lists/${displayId}/display-settings`,
				{ setting: displaySetting }, // Send full CreatorDisplaySetting: { id, displayType, displayId, setting }
			);
			return response.data;
		} catch (error) {
			console.error(`Error updating display settings for display ${displayId}:`, error);
			throw new Error("Failed to update display settings");
		}
	},

	/**
	 * Get display settings for creator groups under a specific block
	 */
	async getGroupDisplaySetting(campaignId: string, blockId: string): Promise<CreatorDisplaySettingResponse> {
		try {
			const response = await axiosInstance.get<CreatorDisplaySettingResponse>(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/display-settings`,
			);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creator group display setting for block ${blockId}:`, error);
			throw new Error("Failed to fetch creator group display setting");
		}
	},

	/**
	 * Update display settings for all creator groups under a specific block
	 */
	async updateGroupDisplaySettings(
		campaignId: string,
		blockId: string,
		displaySetting: CreatorDisplaySettingResponse,
	): Promise<CreatorDisplaySettingResponse> {
		try {
			console.log("📤 Updating group display settings with full object:", displaySetting);
			const response = await axiosInstance.put<CreatorDisplaySettingResponse>(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/display-settings`,
				{ setting: displaySetting },
			);
			return response.data;
		} catch (error) {
			console.error(`Error updating group display settings for block ${blockId}:`, error);
			throw new Error("Failed to update group display settings");
		}
	},

	async updateClientVisibleToCreatorAssignments(
		campaignId: string,
		blockId: string,
		updates: { assignmentId: string; clientVisible: boolean }[],
	): Promise<void> {
		await axiosInstance.put(`/campaign/${campaignId}/block/${blockId}/creator-assignments/client-visible`, {
			updates,
		});
	},

	/**
	 * Get creators from a specific list
	 */
	async getCreatorsFromList(
		campaignId: string,
		blockId: string,
		listId: string,
	): Promise<CreatorWithAssignmentResponse[]> {
		try {
			const response = await axiosInstance.get<CreatorWithAssignmentResponse[]>(
				`/campaign/${campaignId}/block/${blockId}/creator-lists/${listId}/creators`,
			);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creators from list ${listId}:`, error);
			throw new Error("Failed to fetch creators from list");
		}
	},

	async getCreatorDisplaySetting(
		campaignId: string,
		blockId: string,
		displayId: string,
	): Promise<CreatorDisplaySettingResponse> {
		try {
			const response = await axiosInstance.get<CreatorDisplaySettingResponse>(
				`/campaign/${campaignId}/block/${blockId}/creator-lists/${displayId}/display-settings`,
			);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creator display setting for list ${displayId}:`, error);
			throw new Error("Failed to fetch creator display setting");
		}
	},

	/**
	 * Get creator outreach for a specific campaign block
	 */
	async getCreatorOutreach(campaignId: string, blockId: string): Promise<CreatorOutreach[]> {
		try {
			const response = await axiosInstance.get<CreatorOutreach[]>(
				`/campaign/${campaignId}/block/${blockId}/creator-outreaches`,
			);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creator outreach for campaign ${campaignId}, block ${blockId}:`, error);
			throw new Error("Failed to fetch creator outreach");
		}
	},

	/**
	 * Get creators from a specific outreach
	 */
	async getCreatorsFromOutreach(
		campaignId: string,
		blockId: string,
		outreachId: string,
		interestedOnly: boolean = false,
	): Promise<CreatorWithAssignmentResponse[]> {
		try {
			const response = await axiosInstance.get<CreatorWithAssignmentResponse[]>(
				`/campaign/${campaignId}/block/${blockId}/creator-outreaches/${outreachId}/creators`,
				{
					params: interestedOnly ? { interestedOnly: true } : undefined,
				},
			);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creators from outreach ${outreachId}:`, error);
			throw new Error("Failed to fetch creators from outreach");
		}
	},

	async addCreatorsToList(
		campaignId: string,
		blockId: string,
		listId: string,
		urls: string[],
		clientHiddenFields: string[] = [],
		creatorData?: {
			name: string;
			categories: string[];
			tier: string;
			sampleContents: string[];
			rationaleForProposing: string;
			location: string;
		},
	): Promise<void> {
		try {
			await axiosInstance.post(`/campaign/${campaignId}/block/${blockId}/creator-lists/${listId}/creators`, {
				urls,
				clientHiddenFields,
				creatorData,
			});
		} catch (error) {
			console.error(`Error adding creators to list ${listId}:`, error);
			throw error;
		}
	},

	/**
	 * Add a creator to an outreach
	 */
	async addCreatorToOutreach(
		campaignId: string,
		blockId: string,
		outreachId: string,
		assignmentId: string,
	): Promise<void> {
		try {
			await axiosInstance.post(
				`/campaign/${campaignId}/block/${blockId}/creator-outreaches/${outreachId}/creators`,
				{ assignmentId },
			);
		} catch (error) {
			console.error(`Error adding creator to outreach ${outreachId}:`, error);
			throw error;
		}
	},

	/**
	 * Remove a creator from an outreach
	 */
	async removeCreatorFromOutreach(campaignId: string, blockId: string, assignmentId: string): Promise<void> {
		try {
			await axiosInstance.delete(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/outreach`,
			);
		} catch (error) {
			console.error("Error removing creator from outreach:", error);
			throw error;
		}
	},

	/**
	 * Create a new creator group
	 */
	async createCreatorGroup(
		campaignId: string,
		blockId: string,
		name: string,
		description?: string,
		clientVisibility?: boolean,
	): Promise<{ id: string; name: string; description: string; clientVisibility: boolean }> {
		try {
			const response = await axiosInstance.post(`/campaign/${campaignId}/block/${blockId}/creator-groups`, {
				name,
				description,
				clientVisibility,
			});
			return response.data;
		} catch (error) {
			console.error("Error creating creator group:", error);
			throw error;
		}
	},

	/**
	 * Add a creator to a group
	 */
	async addCreatorToGroup(
		campaignId: string,
		blockId: string,
		groupId: string,
		assignmentId: string, // Changed from creatorId to assignmentId
	): Promise<void> {
		try {
			await axiosInstance.post(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/creators`,
				{ assignmentId }, // Changed from creatorId to assignmentId
			);
		} catch (error) {
			console.error("Error adding creator to group:", error);
			throw error;
		}
	},

	/**
	 * Remove a creator from a group
	 */
	async removeCreatorFromGroup(
		campaignId: string,
		blockId: string,
		groupId: string,
		assignmentId: string,
	): Promise<void> {
		try {
			await axiosInstance.delete(`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/creators`, {
				data: { assignmentId },
			});
		} catch (error) {
			console.error("Error removing creator from group:", error);
			throw error;
		}
	},

	/**
	 * Delete a creator group (removes the group and cleans up server-side data).
	 */
	async deleteCreatorGroup(campaignId: string, blockId: string, groupId: string): Promise<void> {
		try {
			await axiosInstance.delete(`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}`);
		} catch (error) {
			console.error("Error deleting creator group:", error);
			throw error;
		}
	},

	/**
	 * Get creators from a specific group
	 */
	async getCreatorsFromGroup(
		campaignId: string,
		blockId: string,
		groupId: string,
	): Promise<CreatorWithAssignmentResponse[]> {
		try {
			const response = await axiosInstance.get<CreatorWithAssignmentResponse[]>(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/creators`,
			);
			return response.data;
		} catch (error) {
			console.error(`Error fetching creators from group ${groupId}:`, error);
			throw new Error("Failed to fetch creators from group");
		}
	},

	/**
	 * Update creator group indices (order)
	 */
	async updateCreatorGroupIndices(
		campaignId: string,
		blockId: string,
		groupIndices: Array<{ groupId: string; index: number }>,
	): Promise<void> {
		try {
			await axiosInstance.put(`/campaign/${campaignId}/block/${blockId}/creator-groups/indices`, {
				groupIndices,
			});
		} catch (error) {
			console.error("Error updating creator group indices:", error);
			throw error;
		}
	},

	/**
	 * Update client visibility for a creator group
	 */
	async updateCreatorGroupClientVisibility(
		campaignId: string,
		blockId: string,
		groupId: string,
		clientVisibility: boolean,
	): Promise<void> {
		try {
			await axiosInstance.put(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/client-visibility`,
				{ clientVisibility },
			);
		} catch (error) {
			console.error("Error updating creator group client visibility:", error);
			throw error;
		}
	},

	/**
	 * Update creator group name
	 */
	async updateCreatorGroupName(campaignId: string, blockId: string, groupId: string, name: string): Promise<void> {
		try {
			await axiosInstance.put(`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/name`, {
				name,
			});
		} catch (error) {
			console.error("Error updating creator group name:", error);
			throw error;
		}
	},

	/**
	 * Save creator rates for a group
	 */
	async saveGroupCreatorRates(
		campaignId: string,
		blockId: string,
		groupId: string,
		creatorRates: Array<{
			assignmentId: string;
			clientVisible: boolean;
			finalRate: number | null;
			finalConvertedRate: number | null;
		}>,
		totalRate: number,
		totalConvertedRate: number,
		finalCurrency: string,
		finalConvertedCurrency: string,
	): Promise<void> {
		try {
			await axiosInstance.put(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/creator-rates`,
				{
					creatorRates,
					totalRate,
					totalConvertedRate,
					finalCurrency,
					finalConvertedCurrency,
				},
			);
		} catch (error) {
			console.error("Error saving group creator rates:", error);
			throw error;
		}
	},

	/**
	 * Add creator to campaign (mark as added_to_campaign = true)
	 */
	async addCreatorToCampaign(
		campaignId: string,
		blockId: string,
		assignmentId: string,
		contentExecutionBlockId: string,
		creatorName: string,
		email?: string | null,
	): Promise<void> {
		try {
			await axiosInstance.post(`/campaign/${campaignId}/block/${blockId}/creator-assignments/add-to-campaign`, {
				assignmentId,
				contentExecutionBlockId,
				creatorName,
				email,
			});
		} catch (error) {
			console.error("Error adding creator to campaign:", error);
			throw error;
		}
	},

	async updateAssignmentContactEmail(
		campaignId: string,
		blockId: string,
		assignmentId: string,
		contactEmail: string | null,
	): Promise<CreatorAssignment> {
		const response = await axiosInstance.put(
			`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/contact-email`,
			{ contactEmail },
		);
		return response.data.assignment;
	},

	async batchAddApprovedCreators(
		campaignId: string,
		blockId: string,
		groupId: string,
		contentExecutionBlockId: string,
		assignmentIds: string[],
	): Promise<{
		approval: GroupApproval;
		added: string[];
		alreadyAdded: string[];
		failed: Array<{ assignmentId: string; error: string }>;
	}> {
		const response = await axiosInstance.post(
			`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/add-approved-creators`,
			{ contentExecutionBlockId, assignmentIds },
		);
		return response.data;
	},

	/**
	 * Remove a creator from campaign
	 */
	async removeCreatorFromCampaign(
		campaignId: string,
		blockId: string,
		assignmentId: string,
		groupId?: string,
	): Promise<void> {
		try {
			await axiosInstance.delete(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/remove-from-campaign`,
				{ data: { assignmentId, groupId } },
			);
		} catch (error) {
			console.error("Error removing creator from campaign:", error);
			throw error;
		}
	},

	/**
	 * Update creator interested status
	 */
	async updateCreatorInterested(
		campaignId: string,
		blockId: string,
		assignmentId: string,
		creatorInterested: CreatorInterestStatus,
	): Promise<void> {
		try {
			await axiosInstance.put(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/creator-interested`,
				{ creatorInterested },
			);
		} catch (error) {
			console.error("Error updating creator interested:", error);
			throw error;
		}
	},

	/**
	 * Update per-creator assignment rates (used by outreach card/table inline editing)
	 */
	async updateCreatorAssignmentRates(
		campaignId: string,
		blockId: string,
		assignmentId: string,
		payload: {
			clientVisible: boolean;
			finalRate: number | null;
			finalConvertedRate?: number | null;
		},
	): Promise<{
		success: boolean;
		assignmentId: string;
		finalRate: number | null;
		finalConvertedRate: number | null;
	}> {
		try {
			const response = await axiosInstance.put(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/rates`,
				payload,
			);
			return response.data;
		} catch (error) {
			console.error("Error updating creator assignment rates:", error);
			throw error;
		}
	},

	/**
	 * Get group approvals
	 */
	async getGroupApprovals(campaignId: string, blockId: string, groupId: string): Promise<GroupApproval[]> {
		try {
			const response = await axiosInstance.get(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/approvals`,
			);
			return response.data;
		} catch (error) {
			console.error("Error getting group approvals:", error);
			throw error;
		}
	},

	/**
	 * Get group approval by current user
	 */
	async getGroupApprovalByUser(campaignId: string, blockId: string, groupId: string): Promise<GroupApproval | null> {
		try {
			const response = await axiosInstance.get(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/approval`,
			);
			return response.data;
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				return null; // No approval found for this user
			}
			console.error("Error getting group approval by user:", error);
			throw error;
		}
	},

	/**
	 * Create group approval
	 */
	async createGroupApproval(
		campaignId: string,
		blockId: string,
		groupId: string,
		status: "approved" | "rejected" | "pending",
		comment?: string,
	): Promise<GroupApproval> {
		try {
			const response = await axiosInstance.post(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/approvals`,
				{
					status,
					comment: comment || "",
				},
			);
			return response.data;
		} catch (error) {
			console.error("Error creating group approval:", error);
			throw error;
		}
	},

	/**
	 * Update group approval comment (creates approval if it doesn't exist)
	 */
	async updateGroupApprovalComment(
		campaignId: string,
		blockId: string,
		groupId: string,
		comment: string,
	): Promise<GroupApproval> {
		try {
			const response = await axiosInstance.put(
				`/campaign/${campaignId}/block/${blockId}/creator-groups/${groupId}/approval/comment`,
				{ comment },
			);
			return response.data;
		} catch (error) {
			console.error("Error updating group approval comment:", error);
			throw error;
		}
	},

	/**
	 * Create creator assignment approval
	 */
	async createCreatorAssignmentApproval(
		campaignId: string,
		blockId: string,
		assignmentId: string,
		status: "approved" | "rejected" | "pending",
		comment?: string,
	): Promise<any> {
		try {
			const response = await axiosInstance.post(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/approvals`,
				{
					status,
					comment: comment || "",
				},
			);
			return response.data;
		} catch (error) {
			console.error("Error creating creator assignment approval:", error);
			throw error;
		}
	},

	/**
	 * Get all creator assignment approvals
	 */
	async getCreatorAssignmentApprovals(
		campaignId: string,
		blockId: string,
		assignmentId: string,
	): Promise<CreatorAssignmentApproval[]> {
		try {
			const response = await axiosInstance.get(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/approvals`,
			);
			return response.data;
		} catch (error) {
			console.error("Error getting creator assignment approvals:", error);
			throw error;
		}
	},

	/**
	 * Get creator assignment approval by current user
	 */
	async getCreatorAssignmentApprovalByUser(
		campaignId: string,
		blockId: string,
		assignmentId: string,
	): Promise<CreatorAssignmentApproval | null> {
		try {
			const response = await axiosInstance.get(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/approval`,
			);
			return response.data;
		} catch (error) {
			console.error("Error getting creator assignment approval by user:", error);
			throw error;
		}
	},

	/**
	 * Update creator assignment approval comment (creates approval if it doesn't exist)
	 */
	async updateCreatorAssignmentApprovalComment(
		campaignId: string,
		blockId: string,
		assignmentId: string,
		comment: string,
	): Promise<CreatorAssignmentApproval> {
		try {
			const response = await axiosInstance.put(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/approval/comment`,
				{ comment },
			);
			return response.data;
		} catch (error) {
			console.error("Error updating creator assignment approval comment:", error);
			throw error;
		}
	},

	async updateRecommendation(
		campaignId: string,
		blockId: string,
		assignmentId: string,
		recommendation: string[],
	): Promise<{ success: boolean }> {
		try {
			const response = await axiosInstance.put(
				`/campaign/${campaignId}/block/${blockId}/creator-assignments/${assignmentId}/recommendation`,
				{ recommendation },
			);
			return response.data;
		} catch (error) {
			console.error("Error updating recommendation:", error);
			throw error;
		}
	},
};

export default creatorListAPI;
